import React, { FC, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Video } from '../../types'
import { Language } from '../../system/apitools'
import SectionWrapper, { SectionStatus } from './LockedSectionWrapper'

// Mirrors the fiber.Map returned by GET /addendum-status.
interface AddendumStatus {
  state: string // "idle" | "running" | "completed" | "failed"
  progress?: number
  languages: string[]
  error?: string
  stage?: string
  logs?: string[]
}

interface AddendumSectionProps {
  video: Video
  languages: Language[]
}

// An addendum is a DIFFERENT, WEAKER claim than the sealed base — see §3.7 of
// docs/plans/2026-08-10-verification-manifest.md and Decision 3 of
// 2026-08-13-verification-integration-closeout.md. The sealed base was
// fingerprinted into the original root hash at the moment of sealing. An
// addendum is the publisher asserting, at a LATER date, that a newly-added
// translation is faithful — signed and immutable once added, but never
// folded into that original root hash. Both are permanent and both are
// publisher-signed, but they are not the same claim, and this copy must never
// blur them into one "verified" story. Nor is an addendum an edit: the sealed
// video underneath is untouched — an addendum only sits alongside it.
const ADDENDUM_NOTICE =
  'This video is sealed, so the languages in its sealed base are frozen. You can still add a ' +
  'language nobody thought of at publication, but as an addendum, not a correction — it does ' +
  "not touch the sealed video at all. An addendum is the publisher asserting, at a later date, " +
  'that a new translation is faithful: signed and permanent once added, but a separate, weaker ' +
  "claim than the sealed base, which was covered by the original root hash. It sits alongside " +
  'the sealed video, never inside it. Once added, an addendum can never be replaced.'

const AddendumSection: FC<AddendumSectionProps> = ({ video, languages }) => {
  const queryClient = useQueryClient()
  const logEndRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [addendumJobId, setAddendumJobId] = useState<string | null>(null)
  const [addendumStatus, setAddendumStatus] = useState<AddendumStatus | null>(null)

  const info = video.publication
  const addenda = info?.addenda || []
  const sealedLangs = info?.sealed_langs || []

  // Offered only if absent from BOTH lists, compared case-insensitively. A
  // mis-cased language code has already caused one real security hole in
  // this feature (an upper-cased addendum shadowing a lower-cased sealed
  // track), so this is the one comparison in the file worth double-checking.
  const takenLangs = new Set(
    [...sealedLangs, ...addenda.map(a => a.lang)].map(l => l.toLowerCase()),
  )
  const options = languages.filter(l => !takenLangs.has(l.code.toLowerCase()))

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [addendumStatus?.logs?.length])

  const addendumMutation = useMutation({
    mutationFn: (langs: string[]) =>
      axios
        .post<Video>(`/api/v1/videos/${video.id}/addendum-subtitles`, { languages: langs })
        .then(res => res.data),
    onSuccess: updated => {
      queryClient.setQueryData(['video', video.id], updated)
      const job = updated.job_history?.find(j => j.type === 'addendum' && j.status === 'running')
      if (job) setAddendumJobId(job.id)
    },
  })

  // Same shape as PublicationSection's re-verify polling: a job id is set
  // once the start call confirms a job is actually running, and this polls
  // until it leaves "running". Unlike re-verify (no progress route — D6),
  // the addendum job has a real one, so this reports percent/stage/logs the
  // same way TranslationsSection does.
  useEffect(() => {
    if (!addendumJobId) return
    const poll = async () => {
      try {
        const res = await axios.get<AddendumStatus>(`/api/v1/videos/${video.id}/addendum-status`)
        setAddendumStatus(res.data)
        if (res.data.state !== 'running') {
          queryClient.invalidateQueries({ queryKey: ['video', video.id] })
          setAddendumJobId(null)
        }
      } catch {
        /* ignore */
      }
    }
    poll()
    const interval = setInterval(poll, 1000)
    return () => clearInterval(interval)
  }, [addendumJobId, video.id, queryClient])

  const toggle = (code: string) => {
    setSelected(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]))
  }

  const isRunning = !!addendumJobId

  const startError = addendumMutation.error
    ? (addendumMutation.error as any)?.response?.data?.error || (addendumMutation.error as Error).message
    : null
  const jobError = addendumStatus?.state === 'failed' ? addendumStatus.error : null
  const errorMessage = startError || jobError

  let status: SectionStatus = 'pending'
  if (isRunning) status = 'in_progress'
  else if (addenda.length > 0) status = 'complete'

  return (
    <SectionWrapper title="Addendum" status={status}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {ADDENDUM_NOTICE}
      </Typography>

      {addenda.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {addenda.map(a => (
            <Chip
              key={a.lang}
              label={`${languages.find(l => l.code.toLowerCase() === a.lang.toLowerCase())?.name || a.lang} ✓`}
              variant="outlined"
              title={
                `Added ${new Date(a.created_at).toLocaleString()} — an addendum, not part of the ` +
                `sealed base` +
                (a.chain ? ' · anchored on chain' : ' · not yet anchored on chain')
              }
            />
          ))}
        </Box>
      )}

      {!isRunning && options.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Every supported language is already covered, either by the sealed base or by an
          existing addendum.
        </Alert>
      )}

      {!isRunning && options.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {options.map(l => (
            <Chip
              key={l.code}
              label={l.name}
              color={selected.includes(l.code) ? 'primary' : 'default'}
              variant={selected.includes(l.code) ? 'filled' : 'outlined'}
              onClick={() => toggle(l.code)}
            />
          ))}
        </Box>
      )}

      {startError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {startError}
        </Alert>
      )}

      {!isRunning && options.length > 0 && (
        <Button
          variant="contained"
          color="secondary"
          disabled={addendumMutation.isPending || selected.length === 0}
          onClick={() => addendumMutation.mutate(selected)}
        >
          {addendumMutation.isPending ? 'Starting...' : `Add addendum (${selected.length})`}
        </Button>
      )}

      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{addendumStatus?.stage || 'Processing...'}</Typography>
          </Box>
          {addendumStatus?.progress !== undefined && addendumStatus.progress > 0 && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress variant="determinate" value={addendumStatus.progress} />
              <Typography variant="caption" color="text.secondary">{addendumStatus.progress}%</Typography>
            </Box>
          )}
          {addendumStatus?.logs && addendumStatus.logs.length > 0 && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#1a1a1a', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {addendumStatus.logs.map((line, i) => (
                <div key={i} style={{ color: '#aaa', whiteSpace: 'pre' }}>{line}</div>
              ))}
              <div ref={logEndRef} />
            </Box>
          )}
        </Box>
      )}

      {jobError && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Typography variant="body2" color="error">
            Addendum failed: {jobError}
          </Typography>
        </Box>
      )}
    </SectionWrapper>
  )
}

export default AddendumSection
